import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_from_agent: boolean;
  created_at: string;
}

interface Conversation {
  id: string;
  user_id: string;
  subject: string | null;
}

/**
 * Hook for setting up realtime listeners for conversations/messages.
 * Handles in-app toast notifications when new messages arrive.
 * Push notifications are handled by the PostgreSQL trigger (notify_on_new_message).
 */
export function useRealtimeNotifications() {
  const { user, isAgent } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const processedMessagesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user) return;

    console.log('[Realtime] Setting up for user:', user.id, 'isAgent:', isAgent);

    // Helper to check if user is currently viewing a specific conversation
    const isViewingConversation = (conversationId: string): boolean => {
      // Check for both agent and user chat routes
      const agentPattern = new RegExp(`^/agent.*${conversationId}`);
      const userPattern = `/chat/${conversationId}`;
      return location.pathname === userPattern || agentPattern.test(location.pathname);
    };

    // Create channel based on user type
    const channelName = isAgent ? 'agent-realtime-notif' : 'user-realtime-notif';
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        async (payload) => {
          const newMsg = payload.new as Message;
          
          // Prevent duplicate processing
          if (processedMessagesRef.current.has(newMsg.id)) {
            return;
          }
          processedMessagesRef.current.add(newMsg.id);
          
          // Limit set size to prevent memory issues
          if (processedMessagesRef.current.size > 100) {
            const entries = Array.from(processedMessagesRef.current);
            processedMessagesRef.current = new Set(entries.slice(-50));
          }

          // Don't notify for own messages
          if (newMsg.sender_id === user.id) {
            return;
          }

          // For users: only care about agent messages
          // For agents: only care about user messages
          const shouldNotify = isAgent ? !newMsg.is_from_agent : newMsg.is_from_agent;
          
          if (!shouldNotify) {
            return;
          }

          // Don't show toast if already viewing this conversation
          if (isViewingConversation(newMsg.conversation_id)) {
            console.log('[Realtime] User is viewing conversation, skipping toast');
            return;
          }

          // Get sender info for the toast
          const { data: senderProfile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', newMsg.sender_id)
            .single();

          const senderName = senderProfile?.first_name 
            ? `${senderProfile.first_name}${senderProfile.last_name ? ` ${senderProfile.last_name}` : ''}`
            : isAgent ? 'Nouveau client' : 'Concierge';

          // Truncate content for preview
          const preview = newMsg.content.length > 50 
            ? newMsg.content.substring(0, 50) + '...' 
            : newMsg.content;

          // Show toast notification
          toast(senderName, {
            description: preview,
            action: {
              label: 'Voir',
              onClick: () => {
                if (isAgent) {
                  navigate(`/agent?conversation=${newMsg.conversation_id}`);
                } else {
                  navigate(`/chat/${newMsg.conversation_id}`);
                }
              },
            },
            duration: 5000,
          });

          console.log('[Realtime] Toast shown for new message:', newMsg.id);
        }
      );

    // Agent also listens for conversation updates
    if (isAgent) {
      channel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
        },
        (payload) => {
          console.log('[Realtime] Conversation updated:', payload.new);
        }
      );
    }

    channel.subscribe();

    return () => {
      console.log('[Realtime] Cleaning up channel:', channelName);
      supabase.removeChannel(channel);
    };
  }, [user, isAgent, location.pathname, navigate]);
}
